import React, { useState, useEffect, useRef } from "react";
import "./ChartDisplay.css";
import config from "../config";
import { useApiAuth } from "../hooks/useApiAuth";
import axios from "axios";

const ChartDisplay = ({ chart, onImageLoad }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState(null);
  const fetchingRef = useRef(false);
  const { getAuthHeaders } = useApiAuth();

  useEffect(() => {
    if (!chart || !chart.imagePath) {
      return;
    }

    // If we already have the image for this imagePath, don't fetch again
    if (currentImagePath === chart.ImagePath && imageSrc) {
      return;
    }

    // If we're already fetching, don't start another fetch
    if (fetchingRef.current) {
      return;
    }

    const fetchImage = async () => {
      try {
        fetchingRef.current = true;
        setError(null);

        // Append config.apiUrl before the relative imagePath
        const imageUrl = `${config.apiUrl}${chart.imagePath}`;

        // Get authentication headers
        const authHeaders = await getAuthHeaders();

        // Fetch the image from the imagePath
        const response = await axios.get(imageUrl, {
          headers: authHeaders,
        });

        // Create data URL from base64 image
        const dataUrl = `data:image/png;base64,${response.data.image}`;
        setImageSrc(dataUrl);
        setCurrentImagePath(chart.imagePath);

        // Notify parent that image is loaded for scrolling
        if (onImageLoad) {
          onImageLoad();
        }
      } catch (err) {
        setError(err.message);
      } finally {
        fetchingRef.current = false;
      }
    };

    fetchImage();

    // No cleanup needed for data URLs
    return () => {
      // Data URLs don't need cleanup
    };
  }, [chart?.imagePath]);

  if (!chart || !chart.imagePath) {
    return null;
  }

  if (error) {
    return (
      <div className="chart-display">
        <div className="chart-image-container">
          <div className="chart-error">Error loading chart: {error}</div>
        </div>
      </div>
    );
  }

  // Only render the container when we have an image
  if (!imageSrc) {
    return null;
  }

  return (
    <div className="chart-display">
      <div className="chart-title">{chart?.title || ""}</div>
      <div className="chart-image-container">
        <img src={imageSrc} alt="Chart" className="chart-image" />
      </div>
    </div>
  );
};

export default ChartDisplay;
